<template>
  <div :class="rootClasses" :style="rootStyles">
    <div class="formester__thankYou" :style="thankYouStyles">
      <div
        v-if="item.showImage"
        class="image-wrapper"
        :style="imageWrapperStyles"
      >
        <img
          v-if="item.image.preview.image"
          :style="imageStyles"
          draggable="false"
          :src="item.image.preview.image"
          :alt="`Image for ${item.image.preview.name}`"
        />
        <i v-else-if="editing" class="fa-regular fa-image placeholder-image" />
      </div>
      <h1
        class="formester__heading"
        :style="thankYouHeadingStyles"
        v-html="label"
      ></h1>
      <SmartText
        :active="active"
        :editing="editing"
        :item="item"
        :style="thankYouDescription"
        :text="description"
        class="formester__subheading"
      />

      <SocialShare
        v-if="item.showSocialShareIcons"
        :color="socialIconColor"
        class="social-media"
        :survey-form-url="surveyFormUrl"
      />

      <a
        :href="item.button.actionUrl"
        :target="item.button.openInNewTab ? '_blank' : '_top'"
      >
        <button
          v-if="item.showActionButton"
          type="submit"
          class="default-style"
          :style="actionButtonStyles"
          @click.prevent="handleClick"
        >
          {{ item.button.label }}
        </button>
      </a>

      <p v-if="paymentId" class="mt-5"><b>Payment ID:</b> {{ paymentId }}</p>

      <p v-if="isRedirectAllowed" class="mt-4">
        Redirecting to
        <a :href="getEncodedRedirectionUrl">
          {{ getEncodedRedirectionUrl }}
        </a>
        in {{ seconds }} seconds.
      </p>
    </div>
  </div>
</template>

<script>
import commonStyles from "../mixins/commonStyles";
import SmartText from "../shared/smart-text.vue";
import sharedActions from "../mixins/sharedActions";
import SocialShare from "@/components/builder/publish/social-share.vue";
import { get } from "lodash";

export default {
  name: "ThankYou",
  components: {
    SmartText,
    SocialShare,
  },
  mixins: [commonStyles, sharedActions],
  props: {
    item: {
      type: Object,
      required: true,
    },
    required: {
      type: Boolean,
      default: false,
    },
    editing: {
      type: Boolean,
      default: false,
    },
    active: {
      type: Boolean,
      default: false,
    },
    styling: {
      type: Object,
      required: true,
    },
    uuid: {
      type: String,
      required: true,
    },
    isPreview: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      seconds: null,
      paymentId: null,
    };
  },
  computed: {
    socialIconColor(){
      return get(this.styling, "buttonColor", "#98A2B3");
    },
    surveyFormUrl() {
      return `https://app.formester.com/f/${this.uuid}`;
    },
    thankYouStyles() {
      let alignment = this.itemStyling.alignment;
      return {
        display: "flex",
        "flex-direction": "column",
        "align-items":
          alignment === "center"
            ? "center"
            : alignment === "left"
              ? "flex-start"
              : "flex-end",
        "text-align": alignment,
      };
    },
    thankYouHeadingStyles() {
      return {
        // default styles
        "overflow-wrap": "break-word",
        // custom styles
        color: this.item.overrideTextColor
          ? this.itemStyling.color
          : this.styling.fontColor || "#575757",
        "font-size": `${this.itemStyling.labelFontSize}px` || "32px",
        "font-family": this.styling.fontFamily || "sans-serif",
        "font-weight": this.itemStyling.labelBold ? "bold" : "normal",
        "font-style": this.itemStyling.labelItalic ? "italic" : "normal",
        "line-height": "150%",
      };
    },
    thankYouDescription() {
      return {
        // default styles
        "overflow-wrap": "break-word",
        // custom styles
        color: this.item.overrideTextColor
          ? this.itemStyling.color
          : this.styling.fontColor || "#575757",
        "font-size": `${this.itemStyling.descriptionFontSize}px` || "16px",
        "font-weight": this.itemStyling.descriptionBold ? "bold" : "normal",
        "font-style": this.itemStyling.descriptionItalic ? "italic" : "normal",
        "font-family": this.styling.fontFamily || "sans-serif",
        "margin-top": `${this.itemStyling.descriptionMarginTop}px` || "8px",
        "margin-Bottom":
          `${this.itemStyling.descriptionMarginBottom}px` || "8px",
        "line-height": "150%",
      };
    },
    imageStyles() {
      return {
        "object-fit": "scale-down",
        "max-width": "100%",
        width: "fit-content",
        background: this.item.image.imgBgColor || "#00000000",
        padding: "5px 0px",
        "border-radius": "2px",
        height: `${this.item.image.imgSize || 100}px`,
      };
    },
    imageWrapperStyles() {
      let alignment = this.itemStyling.alignment;
      return {
        "justify-content":
          alignment === "center"
            ? "center"
            : alignment === "left"
              ? "flex-start"
              : "flex-end",
      };
    },
    actionButtonStyles() {
      return {
        "background-color": this.item.button.overrideColor
          ? this.itemStyling.btnColor
          : this.styling.buttonColor || "#00A3FF",
        "border-color": this.item.button.overrideColor
          ? this.itemStyling.btnColor
          : this.styling.buttonColor || "#00A3FF",
        color: this.item.button.overrideColor
          ? this.itemStyling.btnTextColor
          : this.styling.buttonTextColor || "white",
        "font-family": this.styling.fontFamily || "sans-serif",
        width: this.itemStyling.btnWidth
          ? `${this.itemStyling.btnWidth}px`
          : "fit-content",
        height: this.itemStyling.btnHeight
          ? `${this.itemStyling.btnHeight}px`
          : "fit-content",
        "font-weight": 700,
      };
    },
    isRedirectAllowed() {
      return !this.editing && this.item.redirect && this.item.redirectionUrl;
    },
    getEncodedRedirectionUrl() {
      const url = this.replaceTagWithAnswer(this.item.redirectionUrl);
      return encodeURI(url).replace(/\+/g, "%2B");
    },
  },
  mounted() {
    if (this.isRedirectAllowed) {
      this.redirect();
    }

    const query = new URLSearchParams(window.location.search);
    if (query.has("payment_id")) {
      this.paymentId = query.get("payment_id");
    }
  },
  methods: {
    handleShare(url) {
      window.open(url, "_blank");
    },
    handleClick() {
      if (this.editing || this.isPreview) return;
      // If the current window is inside an iframe
      if (window !== window.parent) {
        window.parent.location.href = this.item.button.actionUrl;
        return;
      }
      window.location = this.item.button.actionUrl;
    },
    redirect() {
      const url = this.getEncodedRedirectionUrl;
      this.seconds = this.item.redirectionDelay;

      if (this.seconds == 0) {
        window.parent.location.href = url;
      } else {
        const interval = setInterval(() => {
          this.seconds--;
          if (this.seconds == 0) {
            clearInterval(interval);
            window.parent.location.href = url;
          }
        }, 1000);
      }
    },
  },
};
</script>

<style scoped>
.image-wrapper {
  width: 100%;
  display: flex;
}
.placeholder-image {
  font-size: 75px;
  opacity: 0.3;
}
.social-media {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin: 12px 0 24px;
}

.formester__thankYou a {
  text-decoration: none;
}
.default-style {
  font-size: 16px;
  border-radius: 3px;
  border: 1px solid;
  padding: 6px 17px;
  outline: none;
  cursor: pointer;
  font-family: "sans-serif";
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 600;
}
.default-style:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}
.default-style:focus {
  border-color: var(--btn-focus-color);
  box-shadow: 0 0 0 0.2rem var(--btn-focus-color);
}
.default-style:active {
  outline: none;
  box-shadow: none;
}
</style>
